import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { ZapIcon, GlobeIcon, ArrowRightIcon, CheckCircleIcon, SearchIcon, MessageSquareIcon, TargetIcon, MapPinIcon, ChevronDownIcon, PhoneIcon, UsersIcon, PhoneOffIcon, CalendarXIcon, TrendingDownIcon, BarChart3Icon, BuildingIcon, LockIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
const faqs = [{
  question: 'What does Viridia Analytics actually do for home service businesses?',
  answer: 'We build predictable growth engines for HVAC, painting, landscaping, and contractor businesses across Phoenix. Our core focus is speed-to-lead: we use AI-assisted automation to respond to inquiries instantly, manage targeted ad campaigns, and build conversion-focused websites to turn more traffic into booked appointments.'
}, {
  question: 'Do you help with lead follow-up after ads start running?',
  answer: 'Yes. That is our primary differentiator. We do not just generate clicks and walk away. We implement AI systems that respond to text messages, lead platform inquiries, and web forms in under 60 seconds, 24/7, ensuring leads do not go cold.'
}, {
  question: 'What is AI lead conversion?',
  answer: 'It is a system where an AI assistant handles initial inbound communication. When a lead reaches out, the AI instantly replies, answers common questions, qualifies the prospect based on your services, and can even book the estimate directly on your calendar.'
}, {
  question: 'Can you work with my current CRM?',
  answer: 'In many cases, yes. We build custom automations to connect your existing tools. If your current CRM is holding you back, we can also migrate you to a centralized command center that handles pipeline visibility, invoicing, and communications in one place.'
}, {
  question: 'Do you only work with Phoenix businesses?',
  answer: 'We are headquartered in Phoenix, Arizona, and specialize in this local market. However, we also partner with home service businesses remotely, including clients in Los Angeles and Long Beach.'
}, {
  question: 'Do you help with Yelp ads?',
  answer: 'Yes. We are a Yelp Advertising Premier Partner. We have direct access to Yelp representatives, priority support, and exclusive insights into bidding strategies to maximize your return on ad spend.'
}, {
  question: 'Can you improve how fast we respond to new leads?',
  answer: 'Absolutely. Our systems are designed to eliminate the delay between a lead reaching out and getting a response. We replace slow human follow-up with instant AI responses and missed-call text-back automation.'
}, {
  question: 'Do I need a new website to work with you?',
  answer: 'No. While we do build high-converting, mobile-first websites, they are optional conversion assets. We can often implement our AI lead conversion and ad management systems using your existing site.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1954">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" aria-expanded={isOpen} data-id="element-1955">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-1956">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-1957" />
      </button>
      <AnimatePresence data-id="element-1958">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1959">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1960">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function HomePage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1961">
      <SEOHead title="AI Marketing Agency Phoenix | Lead Conversion for Home Services | Viridia Analytics" description="Viridia Analytics is a Phoenix AI marketing agency helping home service businesses stop losing leads with AI lead conversion, speed-to-lead automation, and ad management." canonical="/" keywords="marketing agency phoenix, AI marketing agency, AI advertising for contractors, painters, HVAC, landscapers, contractors, window installers, AI lead conversion, local ads management, home services marketing" jsonLd={LOCAL_BUSINESS_SCHEMA} faq={faqs} data-id="element-1962" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 overflow-hidden" data-id="element-1963">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1964">
          <div className="max-w-3xl" data-id="element-1965">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1966">
              Stop Losing Leads You{' '}
              <span className="text-viridia" data-id="element-1967">Already Paid For</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-xl sm:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-1968">
              Viridia Analytics helps home service businesses respond faster,
              follow up smarter, and turn more inbound leads into booked
              appointments - with AI-assisted automation, ad management, and
              conversion-focused systems.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-1969">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-1970">
                See Where Your Leads Are Leaking
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1971" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PARTNER LOGOS */}
      <section className="py-8 bg-viridia-black border-t border-gray-800 overflow-hidden" data-id="element-1972">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 text-center" data-id="element-1973">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1974">
            Trusted by local service businesses
          </p>
        </div>
        <div className="relative flex overflow-x-hidden" data-id="element-1975">
          <motion.div className="flex items-center gap-16 px-8 whitespace-nowrap" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: 40,
          ease: 'linear',
          repeat: Infinity
        }} data-id="element-1976">
            {/* Quadruple the logos to ensure no gaps on ultrawide screens */}
            {[...Array(4)].map((_, i) => <Fragment key={i} data-id="element-1977">
                <img src="https://cdn.magicpatterns.com/uploads/vu3TeukAscWBmXCriEutP3/logo.webp" alt="AZGlo Logo" className="h-20 object-contain rounded-lg" data-id="element-1978" />
                <img src="https://cdn.magicpatterns.com/uploads/oRd3jcQ42CFjrTA2feQhft/logo.jpg" alt="Gomez Landscaping Logo" className="h-20 object-contain rounded-lg" data-id="element-1979" />
                <img src="https://cdn.magicpatterns.com/uploads/oKhtaguHDZQ1jwnsXbrpm4/logo.png" alt="Wall Candy Logo" className="h-20 object-contain rounded-lg" data-id="element-1980" />
                <img src="https://cdn.magicpatterns.com/uploads/qU8ovRMu8iQdUH4ewRRymJ/logo.jpg" alt="TFP Landscaping Logo" className="h-20 object-contain rounded-lg" data-id="element-1981" />
                <img src="https://cdn.magicpatterns.com/uploads/jzSYXmHzY9XrXrg5hvwEvR/logo.jpg" alt="Nuñez Painting Logo" className="h-20 object-contain rounded-lg" data-id="element-1982" />
                <img src="https://cdn.magicpatterns.com/uploads/fGFztLtAhzgRsKrhGD2vLx/logo-01.webp" alt="AQ Communications Logo" className="h-20 object-contain rounded-lg" data-id="element-1983" />
                <img src="https://cdn.magicpatterns.com/uploads/iv4wx5k6BUCRwoL6bVBSqh/Logo.png" alt="MAJ Countertops Logo" className="h-20 object-contain rounded-lg" data-id="element-1984" />
                <img src="https://cdn.magicpatterns.com/uploads/bMUopew95CsBGQxBnkcNtY/logo.jpg" alt="Finish Coat Painting Logo" className="h-20 object-contain rounded-lg" data-id="element-1985" />
              </Fragment>)}
          </motion.div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="py-12 bg-viridia-surface border-y border-gray-800" data-id="element-1986">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1987">
          <ScrollReveal data-id="element-1988">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center" data-id="element-1989">
              <div data-id="element-1990">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1991">242</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1992">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1993">TFP Landscaping</p>
              </div>
              <div data-id="element-1994">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1995">28</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1996">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1997">TFP Landscaping</p>
              </div>
              <div data-id="element-1998">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1999">39.9%</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2000">
                  Close Rate
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-2001">TFP Landscaping</p>
              </div>
              <div data-id="element-2002">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2003">138</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2004">
                  Leads in 30 Days
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2005">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-2006">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2007">29</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2008">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2009">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-2010">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-2011">$15.05k</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-2012">
                  Closed-Won Revenue
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-2013">
                  From AI-worked leads only
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-24 bg-viridia-black" data-id="element-2014">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2015">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-2016">
            <div className="lg:col-span-5" data-id="element-2017">
              <ScrollReveal data-id="element-2018">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2019">
                  Who We Partner With
                </h2>
                <p className="text-xl text-gray-400 leading-relaxed mb-8" data-id="element-2020">
                  We don't work with everyone. Our systems are built
                  specifically for operators who are serious about capturing
                  demand and closing jobs.
                </p>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-7" data-id="element-2021">
              <div className="space-y-6" data-id="element-2022">
                <ScrollReveal delay={0.1} data-id="element-2023">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2024">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2025">
                      <BuildingIcon className="w-5 h-5 text-viridia" data-id="element-2026" />
                    </div>
                    <div data-id="element-2027">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2028">
                        Local Home Service Businesses
                      </h3>
                      <p className="text-gray-400" data-id="element-2029">
                        HVAC, plumbing, landscaping, pest control, painting,
                        roofing, window installers, and general contractors.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.2} data-id="element-2030">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2031">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2032">
                      <TargetIcon className="w-5 h-5 text-viridia" data-id="element-2033" />
                    </div>
                    <div data-id="element-2034">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2035">
                        Companies Investing in Growth
                      </h3>
                      <p className="text-gray-400" data-id="element-2036">
                        Businesses spending meaningfully on ads or actively
                        trying to increase their booked jobs.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
                <ScrollReveal delay={0.3} data-id="element-2037">
                  <div className="flex items-start gap-4 bg-viridia-surface p-6 rounded-xl border border-gray-800" data-id="element-2038">
                    <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-2039">
                      <UsersIcon className="w-5 h-5 text-viridia" data-id="element-2040" />
                    </div>
                    <div data-id="element-2041">
                      <h3 className="text-lg font-bold text-white mb-1" data-id="element-2042">
                        Operators Seeking Better Systems
                      </h3>
                      <p className="text-gray-400" data-id="element-2043">
                        Owners who know they need better follow-up, faster
                        response times, and organized lead handling.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / SYSTEM */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-2044">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2045">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2046">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2047">
              The Speed-to-Lead System
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2048">
              How we turn an inbound inquiry into a booked appointment before
              your competitor even checks their phone.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative" data-id="element-2049">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gray-800 z-0" data-id="element-2050"></div>

            {[{
            step: '1',
            title: 'Lead Comes In',
            desc: 'A prospect finds you via targeted ads or local search and sends a message, form, or calls.'
          }, {
            step: '2',
            title: 'Instant AI Reply',
            desc: 'Within seconds, our AI assistant responds, preventing the lead from moving to the next company.'
          }, {
            step: '3',
            title: 'Qualification',
            desc: 'The AI asks specific questions to qualify the prospect based on your actual service offerings.'
          }, {
            step: '4',
            title: 'Follow-Up',
            desc: 'If they do not book immediately, automated nurture sequences keep your business top-of-mind.'
          }, {
            step: '5',
            title: 'Job Booked',
            desc: 'The qualified estimate is scheduled directly on your calendar. You just show up and close.'
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} className="relative z-10" data-id="element-2051">
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full" data-id="element-2052">
                  <div className="w-12 h-12 rounded-full bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4 text-viridia font-bold" data-id="element-2053">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-2054">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-2055">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* SERVICE HIERARCHY */}
      <section className="py-24 bg-viridia-black" data-id="element-2056">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2057">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2058">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2059">
              Our Core Infrastructure
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2060">
              We build the systems that capture demand and convert it.
            </p>
          </ScrollReveal>

          <div className="space-y-6" data-id="element-2061">
            {/* Primary Service */}
            <ScrollReveal data-id="element-2062">
              <Link to="/ai-lead-conversion" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 hover:border-viridia/50 transition-colors" data-id="element-2063">
                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center" data-id="element-2064">
                  <div className="w-16 h-16 rounded-xl bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-2065">
                    <ZapIcon className="w-8 h-8 text-viridia" data-id="element-2066" />
                  </div>
                  <div className="flex-grow" data-id="element-2067">
                    <div className="text-xs text-viridia font-bold tracking-wide uppercase mb-1" data-id="element-2068">
                      Flagship System
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-viridia transition-colors" data-id="element-2069">
                      AI Lead Conversion & Automations
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-2070">
                      Never miss a lead. We implement AI receptionists, instant
                      text-back, and automated follow-up sequences so every
                      inquiry is handled immediately.
                    </p>
                  </div>
                  <div className="hidden md:flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-full bg-gray-800 group-hover:bg-viridia transition-colors" data-id="element-2071">
                    <ArrowRightIcon className="w-5 h-5 text-white" data-id="element-2072" />
                  </div>
                </div>
              </Link>
            </ScrollReveal>

            {/* Secondary Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2073">
              <ScrollReveal delay={0.1} data-id="element-2074">
                <Link to="/ad-campaigns" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-2075">
                  <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center mb-6" data-id="element-2076">
                    <TargetIcon className="w-6 h-6 text-red-500" data-id="element-2077" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-2078">
                    Ad Campaigns & CPC
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2079">
                    As a Yelp Advertising Premier Partner, we manage targeted ad
                    spend to drive high-intent local traffic directly into your
                    conversion system.
                  </p>
                </Link>
              </ScrollReveal>

              <ScrollReveal delay={0.2} data-id="element-2080">
                <Link to="/web-design-seo" className="group block bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full hover:border-gray-600 transition-colors" data-id="element-2081">
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-2082">
                    <GlobeIcon className="w-6 h-6 text-blue-500" data-id="element-2083" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-gray-300 transition-colors" data-id="element-2084">
                    Conversion Websites
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2085">
                    We build fast, mobile-first websites positioned as
                    conversion assets, not just digital brochures. Designed to
                    turn visitors into booked appointments.
                  </p>
                </Link>
              </ScrollReveal>
            </div>

            {/* Supporting Services */}
            <ScrollReveal delay={0.3} data-id="element-2086">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-2087">
                <h3 className="text-lg font-bold text-white mb-6" data-id="element-2088">
                  Supporting Infrastructure
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2089">
                  <Link to="/crm-business-tools" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2090">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2091">
                      <BarChart3Icon className="w-5 h-5 text-gray-400" data-id="element-2092" />
                      <h4 className="font-bold text-white" data-id="element-2093">CRM Tools</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2094">
                      Centralized pipeline visibility and organization.
                    </p>
                  </Link>
                  <Link to="/reputation-management" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2095">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2096">
                      <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-2097" />
                      <h4 className="font-bold text-white" data-id="element-2098">Reputation</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2099">
                      Automated review requests to build local trust.
                    </p>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 rounded-xl hover:bg-gray-800/50 transition-colors" data-id="element-2100">
                    <div className="flex items-center gap-3 mb-2" data-id="element-2101">
                      <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-2102" />
                      <h4 className="font-bold text-white" data-id="element-2103">SEO / GEO</h4>
                    </div>
                    <p className="text-sm text-gray-400" data-id="element-2104">
                      Local search optimization for discoverability.
                    </p>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* COMPARISON: AGENCIES VS VIRIDIA */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-2105">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2106">
          <ScrollReveal className="max-w-3xl mb-16" data-id="element-2107">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-2108">
              What Most Agencies Sell vs.
              <br data-id="element-2109" />
              What We Actually Fix
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2110">
              We don't just send you clicks and hope for the best. We fix the
              operational gaps where revenue leaks out.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2111">
            {/* The Old Way */}
            <ScrollReveal data-id="element-2112">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full" data-id="element-2113">
                <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-2114">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-2115">
                    <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-2116" />
                  </div>
                  <h3 className="text-2xl font-bold text-white" data-id="element-2117">
                    Most Marketing Agencies
                  </h3>
                </div>
                <ul className="space-y-6" data-id="element-2118">
                  <li className="flex items-start gap-4" data-id="element-2119">
                    <PhoneOffIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2120" />
                    <div data-id="element-2121">
                      <h4 className="font-bold text-white mb-1" data-id="element-2122">
                        Ignore Missed Calls
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2123">
                        They run ads, but if you're on a jobsite and miss the
                        call, that ad spend is wasted.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2124">
                    <TrendingDownIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2125" />
                    <div data-id="element-2126">
                      <h4 className="font-bold text-white mb-1" data-id="element-2127">
                        Send Low-Intent Traffic
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2128">
                        They focus on cheap clicks that clog your pipeline with
                        tire-kickers instead of serious buyers.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-2129">
                    <LockIcon className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-2130" />
                    <div data-id="element-2131">
                      <h4 className="font-bold text-white mb-1" data-id="element-2132">
                        Long-Term Contracts
                      </h4>
                      <p className="text-sm text-gray-400" data-id="element-2133">
                        They lock you into 6 or 12 month retainers with zero
                        visibility into actual performance.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </ScrollReveal>

            {/* The Viridia Way */}
            <ScrollReveal delay={0.2} data-id="element-2134">
              <div className="bg-viridia-black border-2 border-viridia/50 rounded-2xl p-8 h-full relative overflow-hidden" data-id="element-2135">
                <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/5 rounded-full blur-3xl" data-id="element-2136"></div>
                <div className="relative z-10" data-id="element-2137">
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-800" data-id="element-2138">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center" data-id="element-2139">
                      <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-2140" />
                    </div>
                    <h3 className="text-2xl font-bold text-white" data-id="element-2141">
                      Viridia Analytics
                    </h3>
                  </div>
                  <ul className="space-y-6" data-id="element-2142">
                    <li className="flex items-start gap-4" data-id="element-2143">
                      <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2144" />
                      <div data-id="element-2145">
                        <h4 className="font-bold text-white mb-1" data-id="element-2146">
                          Instant AI Follow-Up
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2147">
                          Our AI replies to messages and missed calls in
                          seconds, booking estimates 24/7 so leads stop
                          slipping.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-2148">
                      <BarChart3Icon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2149" />
                      <div data-id="element-2150">
                        <h4 className="font-bold text-white mb-1" data-id="element-2151">
                          Organized Pipeline
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2152">
                          We set up a CRM that shows every lead, every job, and
                          every dollar cleanly, replacing sticky notes and
                          chaos.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4" data-id="element-2153">
                      <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2154" />
                      <div data-id="element-2155">
                        <h4 className="font-bold text-white mb-1" data-id="element-2156">
                          Month-to-Month Partnership
                        </h4>
                        <p className="text-sm text-gray-400" data-id="element-2157">
                          Flexible packages with real reporting. We sit between
                          you and the ad platforms to ensure transparency.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      <section className="py-24 bg-viridia-black border-b border-gray-800" data-id="element-2158">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2159">
          <ScrollReveal data-id="element-2160">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-2161">
              <div className="max-w-2xl" data-id="element-2162">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2163">
                  Documented Results
                </h2>
                <p className="text-lg text-gray-400" data-id="element-2164">
                  Real numbers from real local service businesses.
                </p>
              </div>
              <Link to="/case-studies" className="mt-6 md:mt-0 inline-flex items-center text-white hover:text-gray-300 font-semibold transition-colors" data-id="element-2165">
                View Proof Library
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2166" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2167">
            <ScrollReveal delay={0.1} data-id="element-2168">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-2169">
                <div data-id="element-2170">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-2171">
                    Landscaping
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2172">
                    TFP Landscaping
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2173">
                    <strong data-id="element-2174">Challenge:</strong> 4+ hour response time meant ad
                    spend was being wasted because leads went cold before the
                    owner could reply.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2175">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2176">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2177">284%</p>
                      <p className="text-xs text-gray-400" data-id="element-2178">More Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2179">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2180">242</p>
                      <p className="text-xs text-gray-400" data-id="element-2181">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2182">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2183">28</p>
                      <p className="text-xs text-gray-400" data-id="element-2184">AI Bookings</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2185">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-2186">39.9%</p>
                      <p className="text-xs text-gray-400" data-id="element-2187">Close Rate</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-2188">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2189" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2190">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-2191">
                <div data-id="element-2192">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-2193">
                    Painting
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2194">
                    Finish Coat Painting
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2195">
                    <strong data-id="element-2196">Challenge:</strong> Generating strong inbound demand
                    but couldn't keep up with conversations. Leads were slipping
                    through the cracks.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2197">
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2198">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2199">29</p>
                      <p className="text-xs text-gray-400" data-id="element-2200">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2201">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2202">
                        $15.05k
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-2203">Closed-Won</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2204">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2205">138</p>
                      <p className="text-xs text-gray-400" data-id="element-2206">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2207">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-2208">64%</p>
                      <p className="text-xs text-gray-400" data-id="element-2209">Lead Growth</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-2210">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2211" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2212">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2213">
          <ScrollReveal data-id="element-2214">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2215">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2216">
          <ScrollReveal delay={0.2} data-id="element-2217">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2218">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2219" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2220">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2221">
          <ScrollReveal data-id="element-2222">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-2223">
              See where your leads are leaking
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-2224">
              Book a real strategy call. We'll look at your ads, website, and
              follow-up process to find exactly where you are losing jobs.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-viridia-black bg-white rounded-xl hover:bg-gray-200 transition-all duration-300" data-id="element-2225">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-6 h-6" data-id="element-2226" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}